// src/contexts/VirtualLabDatasetContext.js
//
// Shared "active dataset" workspace context for the Virtual Lab. Mounted once
// in App.js, above the router, so the selection survives navigation between
// EDA / ML Training / future modules even though each Virtual Lab route
// remounts its own <VirtualLabLayout> (see App.js's per-route element trees).
//
// This context owns dataset *selection*, not dataset *content* - each module
// still calls useDatasetData(selectedVersionId || 'main', versions) itself to
// fetch the actual rows for its own purposes.
import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import apiClient from '../services/apiClient';

const VirtualLabDatasetContext = createContext();

const STORAGE_KEY = 'vl.datasetContext.selectedVersionId';

const readStoredSelection = () => {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (raw == null) return null;
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
};

export const useVirtualLabDataset = () => {
    const context = useContext(VirtualLabDatasetContext);
    if (!context) {
        throw new Error('useVirtualLabDataset must be used within a VirtualLabDatasetProvider');
    }
    return context;
};

export const VirtualLabDatasetProvider = ({ children }) => {
    const [versions, setVersions] = useState([]);
    const [versionsLoading, setVersionsLoading] = useState(false);
    const [versionsError, setVersionsError] = useState(null);

    const [pendingData, setPendingData] = useState([]);
    const [pendingLoading, setPendingLoading] = useState(false);
    const [pendingError, setPendingError] = useState(null);

    const [selectedVersionId, setSelectedVersionId] = useState(() => readStoredSelection());
    const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Guards against re-fetching forever when a fetch legitimately returns an
    // empty list, and against VirtualLabLayout's per-route remounts re-firing
    // the fetch on every module switch.
    const hasFetchedVersionsRef = useRef(false);
    const hasFetchedPendingRef = useRef(false);

    // Only one module is mounted at a time under the current routing model,
    // so a single ref (not a registry) is enough to hold "the current
    // module's pre-switch check", if it has one. A plain ref (not state) is
    // used because swapping the guard function shouldn't trigger a re-render.
    const guardRef = useRef(null);

    const fetchVersions = useCallback(async () => {
        setVersionsLoading(true);
        setVersionsError(null);
        try {
            const res = await apiClient.get('/versions');
            if (res.ok) {
                setVersions(await res.json());
            } else {
                setVersionsError('Failed to load saved datasets.');
            }
        } catch (e) {
            console.error('Failed to load versions', e);
            setVersionsError('Failed to load saved datasets.');
        } finally {
            setVersionsLoading(false);
            hasFetchedVersionsRef.current = true;
        }
    }, []);

    const fetchPending = useCallback(async () => {
        setPendingLoading(true);
        setPendingError(null);
        try {
            const res = await apiClient.get('/data/pending');
            if (res.ok) {
                setPendingData(await res.json());
            } else {
                setPendingError('Failed to load provisional data.');
            }
        } catch (e) {
            console.error('Failed to load pending data', e);
            setPendingError('Failed to load provisional data.');
        } finally {
            setPendingLoading(false);
            hasFetchedPendingRef.current = true;
        }
    }, []);

    const ensureVersionsLoaded = useCallback(() => {
        if (versionsLoading || hasFetchedVersionsRef.current) return;
        fetchVersions();
    }, [versionsLoading, fetchVersions]);

    const ensurePendingLoaded = useCallback(() => {
        if (pendingLoading || hasFetchedPendingRef.current) return;
        fetchPending();
    }, [pendingLoading, fetchPending]);

    const refreshVersions = useCallback(() => {
        hasFetchedVersionsRef.current = false;
        fetchVersions();
    }, [fetchVersions]);

    const refreshPending = useCallback(() => {
        hasFetchedPendingRef.current = false;
        fetchPending();
    }, [fetchPending]);

    // If the previously-selected version was deleted elsewhere, fall back to
    // Master rather than leaving the UI pointed at a dataset that no longer
    // exists.
    useEffect(() => {
        if (!hasFetchedVersionsRef.current) return;
        if (typeof selectedVersionId !== 'number') return;
        const stillExists = versions.some((v) => v.id === selectedVersionId);
        if (!stillExists) {
            setSelectedVersionId(null);
            sessionStorage.removeItem(STORAGE_KEY);
        }
        // eslint-disable-next-line
    }, [versions]);

    const registerSwitchGuard = useCallback((guardFn) => {
        guardRef.current = guardFn;
        return () => {
            if (guardRef.current === guardFn) {
                guardRef.current = null;
            }
        };
    }, []);

    const selectVersion = useCallback(async (nextId) => {
        if (nextId === selectedVersionId) return;

        if (guardRef.current) {
            const proceed = await guardRef.current(nextId);
            if (!proceed) return; // nothing was committed yet, so there's nothing to roll back
        }

        setSelectedVersionId(nextId);
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(nextId));
        } catch (e) {
            // sessionStorage unavailable (e.g. private browsing) - selection
            // still works for the current tab, it just won't survive a refresh.
        }
    }, [selectedVersionId]);

    const toggleCollapsed = useCallback(() => setIsPanelCollapsed((c) => !c), []);

    const value = {
        versions,
        versionsLoading,
        versionsError,
        pendingData,
        pendingLoading,
        pendingError,
        hasUserSubmissions: pendingData.length > 0,
        pendingCount: pendingData.length,
        selectedVersionId,
        isPanelCollapsed,
        searchTerm,
        setSearchTerm,
        toggleCollapsed,
        setCollapsed: setIsPanelCollapsed,
        selectVersion,
        ensureVersionsLoaded,
        ensurePendingLoaded,
        refreshVersions,
        refreshPending,
        registerSwitchGuard,
    };

    return (
        <VirtualLabDatasetContext.Provider value={value}>
            {children}
        </VirtualLabDatasetContext.Provider>
    );
};

export default VirtualLabDatasetContext;
